import dayjs from "dayjs";
import {
  extractStockTaxRows,
  extractStockTaxSummaryTotal,
  parseStockTaxReport,
} from "../../../utils/equity/stockTaxReport";
import { formatReportTypeColumnLabel } from "../../../utils/reports/reportDisplayLabels";
import { withTxnBenchmarkFields } from "../../../utils/reports/transactionBenchmarkColumns";
import { withTxnMetadataFields } from "../../../utils/reports/transactionMetadataColumns";

export type UserMeta = Record<
  string | number,
  {
    userId: number;
    userName: string;
    aumUserId?: string;
    email?: string;
    mobile?: string;
    userType?: number;
  }
>;

export const buildUserMetaFromInvestors = (
  users: {
    id: number;
    userId?: number;
    firstName: string;
    aumUserId?: string;
    email?: string;
    mobile?: string;
    userType?: number;
  }[],
  selectedIds: number[],
): UserMeta => {
  const meta: UserMeta = {};
  for (const id of selectedIds) {
    const u = users.find((x) => (x.userId ?? x.id) === id);
    if (!u) continue;
    meta[id] = {
      userId: u.userId ?? u.id,
      userName: u.firstName,
      aumUserId: u.aumUserId,
      email: u.email,
      mobile: u.mobile,
      userType: u.userType,
    };
  }
  return meta;
};

export const parseHoldingsDateRangeTimestamps = (
  start: unknown,
  end: unknown,
): { startDate: number; endDate: number } | null => {
  if (
    typeof start !== "string" ||
    typeof end !== "string" ||
    !/^\d{4}-\d{2}-\d{2}$/.test(start) ||
    !/^\d{4}-\d{2}-\d{2}$/.test(end)
  ) {
    return null;
  }
  return {
    startDate: dayjs(start).startOf("day").valueOf(),
    endDate: dayjs(end).endOf("day").valueOf(),
  };
};

export const isMfP2pDateRangeComplete = (start: unknown, end: unknown) =>
  typeof start === "string" &&
  typeof end === "string" &&
  /^\d{4}-\d{2}-\d{2}$/.test(start) &&
  /^\d{4}-\d{2}-\d{2}$/.test(end);

const formatEpochDate = (value: unknown): string => {
  if (value == null || value === "" || value === "-") return "-";
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) {
    return dayjs(n).format("DD/MM/YYYY");
  }
  return String(value);
};

const getTxnTypeRaw = (row: Record<string, any>): string =>
  String(
    row.transType ??
      row.txnType ??
      row.transactionType ??
      row.transactiontype ??
      "",
  ).trim();

const isSellTxn = (row: Record<string, any>): boolean => {
  const raw = getTxnTypeRaw(row).replace(/\s+/g, "_").toUpperCase();
  return raw === "SELL" || raw === "REDEMPTION" || raw === "REDEEM";
};

/** Mirrors aumsampaan `mapMfTxnSeparatePnlFields`: keep only the relevant P&L per row type. */
const mapMfTxnSeparatePnlFields = (
  row: Record<string, any>,
  isSell: boolean,
): Record<string, any> => {
  const unrealPnlRaw =
    row.unrealisedPNL ?? row.unrealisedPnL ?? row.unrealizedPNL ?? row.unrealizedPnl;
  const unrealPercRaw =
    row.unrealisedPNLPercentage ??
    row.unrealisedPnLPercentage ??
    row.unrealizedPNLPercentage ??
    row.unrealizedPnLPercentage;
  const realisedPnlRaw =
    row.realisedPnl ??
    row.realisedPnL ??
    row.realisedPNL ??
    row.realizedPnl ??
    row.realizedPnL ??
    row.realizedPNL;
  const realisedPercRaw =
    row.realisedPNLPercentage ??
    row.realisedPnLPercentage ??
    row.realizedPNLPercentage ??
    row.realizedPnLPercentage;

  return {
    unrealisedPNL: isSell ? "-" : unrealPnlRaw ?? "-",
    unrealisedPNLPercentage: isSell ? "-" : unrealPercRaw ?? "-",
    realisedPnl: isSell ? realisedPnlRaw ?? "-" : "-",
    realisedPNLPercentage: isSell ? realisedPercRaw ?? "-" : "-",
  };
};

/** Mirrors aumsampaan `getHoldingSchemeName`. */
const getHoldingSchemeName = (
  h: Record<string, any>,
  hMeta?: Record<string, any>,
): string | undefined => {
  if (!h || typeof h !== "object") return undefined;
  const meta = hMeta ?? h.metadata ?? h.meta ?? {};
  return (
    h.schemeName ??
    h.symbol ??
    h.name ??
    h.holdingName ??
    h.assetName ??
    meta.schemeName ??
    undefined
  );
};

/** Mirrors aumsampaan `getHoldingIsin`. */
const getHoldingIsin = (
  h: Record<string, any>,
  hMeta?: Record<string, any>,
): string | undefined => {
  if (!h || typeof h !== "object") return undefined;
  const meta = hMeta ?? h.metadata ?? h.meta ?? {};
  return (
    h.isin ??
    h.ISIN ??
    h.schemeCode ??
    meta.isin ??
    meta.ISIN ??
    meta.schemeCode ??
    undefined
  );
};

const normalizeUnrealisedRow = (row: Record<string, any>) => ({
  ...row,
  schemeName: row.schemeName ?? row.name,
  units: row.units ?? row.closingBalance,
  costValue: row.costValue ?? row.purchaseValue ?? row.investedAmount ?? row.amount,
  currentNAV: row.currentNAV ?? row.currentNav ?? row.nav,
  currentValue: row.currentValue ?? row.currentAmount ?? row.marketValue,
  unrealisedPNL:
    row.unrealisedPNL ??
    row.unrealisedPnL ??
    row.unrealizedPNL ??
    row.gainLoss,
  unrealisedPNLPercentage:
    row.unrealisedPNLPercentage ??
    row.unrealisedPnLPercentage ??
    row.unrealizedPNLPercentage ??
    row.gainLossPercentage ??
    row.gainLossPerc,
  purchaseDate: formatEpochDate(row.purchaseDate ?? row.transactionDate),
  currentDate: formatEpochDate(row.currentDate ?? Date.now()),
});

const normalizeRealisedRow = (row: Record<string, any>): Record<string, any> => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const avgBuy =
    row.purchaseNAV ?? row.purchaseNav ?? row.avgBuyPrice ?? row.averageBuyPrice;
  const sellNav = row.currentNAV ?? row.currentNav ?? row.nav;
  return {
    ...row,
    schemeName: getHoldingSchemeName(row, meta) ?? row.schemeName ?? row.name,
    isin: getHoldingIsin(row, meta) ?? "-",
    type: "Realized",
    transactionDate: formatEpochDate(row.transactionDate ?? row.transactiondate),
    purchaseNAV: sellNav ?? row.purchaseNAV,
    avgBuyPrice: avgBuy ?? "-",
    saleValue: row.saleValue ?? row.currentValue ?? row.amount,
    realisedPnl:
      row.realisedPnl ?? row.realisedPnL ?? row.realisedPNL ?? row.realizedPnl,
    realisedPNLPercentage:
      row.realisedPNLPercentage ??
      row.realisedPnLPercentage ??
      row.realizedPNLPercentage,
  };
};

const getHoldingTransactions = (holding: Record<string, any>): any[] => {
  const list =
    holding.transactions ??
    holding.transaction ??
    holding.txns ??
    holding.trxn ??
    [];
  return Array.isArray(list) ? list : [];
};

const flattenMfHoldingsTransactions = (holdings: any[]): any[] => {
  const txns: any[] = [];
  for (const h of holdings || []) {
    if (!h || typeof h !== "object") continue;
    const hMeta = h.metadata ?? h.meta ?? {};
    const schemeName = getHoldingSchemeName(h, hMeta);
    const holdingIsin = getHoldingIsin(h, hMeta);
    const list = getHoldingTransactions(h);
    if (!list.length) continue;
    for (const t of list) {
      const meta = t.metadata ?? t.meta ?? {};
      const isSell = isSellTxn(t);
      txns.push(
        withTxnMetadataFields(
          withTxnBenchmarkFields({
            ...t,
            schemeName: t.schemeName ?? schemeName,
            isin: t.isin ?? t.ISIN ?? holdingIsin ?? "-",
            folioNumber:
              t.folioNumber ?? t.folioNo ?? t.folio ?? h.folioNumber ?? h.folioNo,
            userId: t.userId ?? h.userId,
            memberName: t.memberName ?? h.memberName,
            transactionDate: formatEpochDate(
              t.transactionDate ?? t.transactiondate ?? t.date,
            ),
            currentDate: formatEpochDate(
              t.currentDate ?? h.currentDate ?? Date.now(),
            ),
            transType: t.transType ?? t.txnType ?? t.transactionType,
            purchaseNAV:
              t.purchaseNAV ?? t.purchaseNav ?? t.nav ?? meta.nav ?? meta.purchaseNAV,
            currentNAV: t.currentNAV ?? t.currentNav ?? meta.currentNAV,
            avgBuyPrice:
              t.avgBuyPrice ??
              t.averageBuyPrice ??
              t.avgPurchasePrice ??
              meta.avgBuyPrice ??
              meta.averageBuyPrice,
            costValue:
              t.costValue ?? t.netPurchase ?? t.amount ?? t.investedAmount ?? meta.costValue,
            currentValue: isSell
              ? "-"
              : t.currentValue ?? t.currentAmount ?? meta.currentValue,
            currentAmount: isSell
              ? "-"
              : t.currentAmount ?? t.currentValue ?? meta.currentAmount,
            units: t.units ?? t.quantity,
            days: t.days ?? t.avgDays,
            ...mapMfTxnSeparatePnlFields(t, isSell),
          }),
        ),
      );
    }
  }
  return txns;
};

const filterPositiveUnits = (rows: any[]) =>
  rows.filter((r) => Number(r?.units ?? r?.quantity ?? 0) > 0);

const filterRealizedTxnRows = (rows: any[]) => rows.filter((r) => isSellTxn(r));

const formatMfTxnTypeLabel = (row: Record<string, any>): string => {
  const raw = getTxnTypeRaw(row);
  const upper = raw.replace(/\s+/g, "_").toUpperCase();
  if (upper === "BUY" || upper === "PURCHASE") return "Buy";
  if (upper === "SELL" || upper === "REDEMPTION" || upper === "REDEEM") return "Sell";
  return raw;
};

/** Mirrors aumsampaan `mapMfTransactionReportRow` (fund/getTransactions rows). */
const mapTransactionDetailsRow = (row: Record<string, any>) => {
  const isSell = isSellTxn(row);
  const resolvedType = formatMfTxnTypeLabel(row);
  const meta = row?.metadata ?? row?.meta ?? {};
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      transactiondate: formatEpochDate(
        row.transactiondate ?? row.transactionDate ?? row.trxnDate ?? row.date,
      ),
      schemeName: row.schemeName ?? row.scheme_name ?? row.name,
      category: row.category ?? row.fundCategory ?? meta.category ?? "-",
      folioNo: row.folioNo ?? row.folioNumber ?? row.folio,
      transactionType:
        row.transactionType ?? resolvedType ?? (isSell ? "Sell" : "Buy"),
      transType: row.transType ?? resolvedType,
      currentDate: formatEpochDate(row.currentDate ?? Date.now()),
      amount:
        row.amount ??
        row.netPurchase ??
        row.costValue ??
        row.investedAmount,
      netPurchase:
        row.netPurchase ??
        row.amount ??
        row.costValue ??
        row.investedAmount,
      currentValue: row.currentValue ?? row.currentAmount ?? row.currentVal ?? "-",
      days: row.days ?? row.averageDays ?? row.avgDays ?? meta.days,
      cagr: resolveHoldingCagr(row),
    }),
  );
};

const getMfTxnDateSortValue = (row: Record<string, any>): number => {
  const raw =
    row.transactiondate ?? row.transactionDate ?? row.trxnDate ?? row.date;
  if (raw == null || raw === "") return 0;
  const str = String(raw).trim();
  const dmy = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmy) {
    const [, dd, mm, yyyy] = dmy;
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd)).getTime();
  }
  const num = Number(str);
  if (Number.isFinite(num) && num > 0) return num > 1e12 ? num : num * 1000;
  const parsed = Date.parse(str);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortMfTransactionsByDateDesc = (rows: any[]): any[] =>
  [...rows].sort(
    (a, b) => getMfTxnDateSortValue(b) - getMfTxnDateSortValue(a),
  );

/** Extract rows from fund/returnsReport payload (array or nested `data`). */
const extractWeeklyReturnsRows = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.response)) return payload.response;
  return [];
};

/** Mirrors holdings `MutualFundReturnsReport` row shape for MFWeeklyReturns columns. */
const normalizeWeeklyReturnsRow = (row: Record<string, any>) =>
  withTxnMetadataFields({
    ...row,
    schemeName: row.schemeName ?? row.name ?? "-",
    units: row.units ?? row.quantity ?? row.closingBalance ?? 0,
    amount: row.amount ?? row.investedAmount ?? row.costValue ?? 0,
    currentVal:
      row.currentVal ?? row.currentValue ?? row.currentAmount ?? row.marketValue ?? 0,
    currentNav: row.currentNav ?? row.currentNAV ?? row.current_nav,
    weeklyReturn: row.weeklyReturn ?? row.weekelyReturn ?? 0,
    weeklyReturnPercentage:
      row.weeklyReturnPercentage ?? row.weekelyReturnPercentage ?? 0,
    weekelyReturn: row.weekelyReturn ?? row.weeklyReturn ?? 0,
    weekelyReturnPercentage:
      row.weekelyReturnPercentage ?? row.weeklyReturnPercentage ?? 0,
  });

const sortWeeklyReturnsRows = (rows: any[]): any[] =>
  [...rows].sort(
    (a, b) =>
      Number(b.weeklyReturnPercentage ?? b.weekelyReturnPercentage ?? 0) -
      Number(a.weeklyReturnPercentage ?? a.weekelyReturnPercentage ?? 0),
  );

const flattenSectorCapRows = (rows: any[]): any[] => {
  const out: any[] = [];
  for (const row of rows || []) {
    if (!row || typeof row !== "object") continue;
    if (Array.isArray(row.sectors)) {
      for (const s of row.sectors) {
        out.push({ ...s, memberName: row.memberName ?? s.memberName });
      }
    } else {
      out.push(row);
    }
  }
  return out;
};

const buildMetaByUserName = (userMeta: UserMeta): Record<string, UserMeta[string]> => {
  const byName: Record<string, UserMeta[string]> = {};
  for (const meta of Object.values(userMeta)) {
    if (meta.userName) {
      byName[meta.userName.trim().toLowerCase()] = meta;
    }
  }
  return byName;
};

const resolveInvestorMeta = (
  row: Record<string, any>,
  userMeta: UserMeta,
  metaByUserName: Record<string, UserMeta[string]>,
  singleSelectedMeta: UserMeta[string] | null,
): UserMeta[string] | null => {
  const uid = row?.userId;
  if (uid != null) {
    const fromId = userMeta[uid] ?? userMeta[String(uid)];
    if (fromId) return fromId;
  }

  const memberName = row?.memberName;
  if (memberName != null && String(memberName).trim() !== "") {
    const fromMember = metaByUserName[String(memberName).trim().toLowerCase()];
    if (fromMember) return fromMember;
  }

  if (singleSelectedMeta) return singleSelectedMeta;
  return null;
};

const resolveInvestorDisplay = (
  meta: UserMeta[string] | null,
  row: Record<string, any>,
): string => {
  if (meta) {
    return `${meta.userName} (${meta.aumUserId ?? meta.userId})`;
  }
  const memberName = row?.memberName;
  if (memberName != null && String(memberName).trim() !== "") {
    return String(memberName);
  }
  const userName = row?.userName;
  if (userName != null && String(userName).trim() !== "") {
    const aumUserId = row?.aumUserId;
    return aumUserId != null && String(aumUserId).trim() !== ""
      ? `${userName} (${aumUserId})`
      : String(userName);
  }
  return "-";
};

const resolveHoldingAverageDays = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const val =
    row.averageDays ??
    row.averageDay ??
    row.days ??
    row.totalTimeInDays ??
    meta.averageDays ??
    meta.averageDay ??
    meta.days;
  if (val == null || val === "") return "-";
  return val;
};

const resolveTxnDays = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const val = row.days ?? row.avgDays ?? meta.days;
  if (val == null || val === "") return "-";
  return val;
};

const resolveHoldingCagr = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  for (const key of ["cagr", "cagrMF", "cagrMFAumCapital"] as const) {
    const val = row[key] ?? meta[key];
    if (val != null && val !== "") return val;
  }
  return row.cagr ?? "-";
};

const resolveMfUnrealizedPurchasePrice = (
  row: Record<string, any>,
): string | number => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  const direct =
    row.averageNav ??
    row.averageNAV ??
    row.avgNav ??
    row.avgNAV ??
    row.purchaseNAV ??
    row.purchaseNav ??
    row.purchasePrice ??
    row.avgPrice ??
    meta.averageNav ??
    meta.averageNAV ??
    meta.purchaseNAV ??
    meta.purchasePrice;
  if (direct != null && direct !== "" && direct !== "-") {
    const n = Number(direct);
    if (Number.isFinite(n)) return n;
    return direct;
  }
  const units = Number(row.units ?? row.closingBalance ?? row.quantity ?? meta.units);
  const cost = Number(
    row.costValue ?? row.investedAmount ?? row.amount ?? meta.costValue,
  );
  if (Number.isFinite(units) && units > 0 && Number.isFinite(cost)) {
    return cost / units;
  }
  return "-";
};

const enrichUnrealizedHoldingRow = (row: Record<string, any>) => {
  const meta = row?.metadata ?? row?.meta ?? {};
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      type: formatReportTypeColumnLabel(
        (row.type as string | null | undefined) ??
          (row.transType as string | null | undefined),
        "Unrealized",
      ),
      days: resolveHoldingAverageDays(row),
      cagr: resolveHoldingCagr(row),
      purchasePrice: resolveMfUnrealizedPurchasePrice(row),
      adjustedCostPrice:
        row.adjustedCostPrice ?? meta.adjustedCostPrice ?? row.adjCost,
    }),
  );
};

/** Mirrors aumsampaan `normalizeHoldingRowForAll` (segment = Mutual Funds). */
const normalizeHoldingRowForAll = (row: Record<string, any>) => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const schemeName = getHoldingSchemeName(row, meta) ?? row.schemeName ?? row.name;
  const units = row.units ?? row.quantity ?? row.qty ?? row.closingBalance;
  const costValue =
    row.costValue ??
    row.investedAmount ??
    row.amount ??
    row.purchaseValue ??
    row.invested_amount;
  const currentValue =
    row.currentValue ?? row.currentAmount ?? row.marketValue ?? row.market_value;
  const pnl =
    row.unrealisedPNL ??
    row.unrealisedPnL ??
    row.unrealizedPNL ??
    row.unrealisedGainLoss ??
    row.unrealizedGainLoss ??
    row.gainLoss;
  const pnlPct =
    row.unrealisedPNLPercentage ??
    row.unrealisedPnLPercentage ??
    row.unrealizedPNLPercentage ??
    row.gainLossPercentage ??
    row.gainLossPerc;
  const averageNav = resolveMfUnrealizedPurchasePrice(row);
  const currentNAV =
    row.currentNAV ?? row.currentNav ?? row.current_nav ?? row.nav ?? meta.currentNAV ?? meta.currentNav;

  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName,
      isin: getHoldingIsin(row, meta) ?? "-",
      type: formatReportTypeColumnLabel("Unrealized", "Unrealized"),
      transactionDate: formatEpochDate(
        row.transactionDate ?? row.transactiondate ?? row.date ?? row.purchaseDate,
      ),
      folioNumber: row.folioNumber ?? row.folioNo ?? row.folio ?? "-",
      units,
      averageNav: averageNav ?? "-",
      currentNAV: currentNAV ?? "-",
      days: resolveHoldingAverageDays(row),
      cagr: resolveHoldingCagr(row),
      costValue: costValue ?? "-",
      currentValue: currentValue ?? "-",
      adjustedCostPrice: row.adjustedCostPrice ?? meta.adjustedCostPrice,
      gainLoss: pnl ?? "-",
      gainLossPercentage: pnlPct ?? "-",
    }),
  );
};

/** Mirrors aumsampaan `normalizeTxnRowForAll` (segment = Mutual Funds). */
const normalizeTxnRowForAll = (row: Record<string, any>) => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const schemeName =
    row.schemeName ?? getHoldingSchemeName(row, meta) ?? row.name;
  const units = row.units ?? row.quantity ?? row.qty;
  const currentValue = row.currentValue ?? row.currentAmount ?? row.currentVal ?? "-";
  const averageNav =
    row.avgBuyPrice ??
    row.averageBuyPrice ??
    row.avgPurchasePrice ??
    meta.avgBuyPrice ??
    meta.averageBuyPrice ??
    row.purchaseNAV ??
    row.purchaseNav ??
    row.nav ??
    meta.purchaseNAV;
  const currentNAV =
    row.currentNAV ?? row.currentNav ?? row.current_nav ?? meta.currentNAV ?? meta.currentNav;
  const pnl =
    row.realisedPnl ??
    row.realisedPnL ??
    row.realisedPNL ??
    row.realizedPnl ??
    row.realizedPNL ??
    row.realisedGainLoss ??
    row.realizedGainLoss;
  const pnlPct =
    row.realisedPNLPercentage ??
    row.realisedPnLPercentage ??
    row.realizedPNLPercentage ??
    row.realizedPnLPercentage;

  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName,
      isin: getHoldingIsin(row, meta) ?? "-",
      type: formatReportTypeColumnLabel("Realized", "Realized"),
      transactionDate:
        row.transactionDate ?? row.transactiondate ?? row.date ?? "-",
      folioNumber: row.folioNumber ?? row.folioNo ?? row.folio ?? "-",
      units,
      averageNav: averageNav ?? "-",
      currentNAV: currentNAV ?? "-",
      days: resolveTxnDays(row),
      cagr: resolveHoldingCagr(row),
      costValue: "-",
      currentValue,
      gainLoss: pnl ?? "-",
      gainLossPercentage: pnlPct ?? "-",
      adjustedCostPrice:
        row.adjustedCostPrice ?? meta.adjustedCostPrice ?? row.adjCost,
    }),
  );
};

export const attachInvestorMeta = (
  rows: any[],
  userMeta: UserMeta,
  selectedUserIds: number[] = [],
): any[] => {
  if (!Array.isArray(rows)) return [];
  const metaByUserName = buildMetaByUserName(userMeta);
  const singleSelectedMeta =
    selectedUserIds.length === 1
      ? userMeta[selectedUserIds[0]] ?? userMeta[String(selectedUserIds[0])] ?? null
      : null;

  return rows.map((row) => {
    const meta = resolveInvestorMeta(
      row,
      userMeta,
      metaByUserName,
      singleSelectedMeta,
    );
    const display = resolveInvestorDisplay(meta, row);
    return {
      ...row,
      userId: row.userId ?? meta?.userId,
      investorDisplay: display,
      investorEmail: meta?.email ?? "",
      investorMobile: meta?.mobile ?? "",
      userName: meta?.userName ?? row.userName ?? row.memberName,
      aumUserId: meta?.aumUserId ?? row.aumUserId,
    };
  });
};

export type ParsedMfReport = {
  rows: any[];
  totalHoldingInfo?: Record<string, unknown> | null;
  totalPages?: number;
  pageIndex?: number;
  totalRecords?: number;
  taxTotalGainLoss?: number | null;
};

export const parseMfReportResponse = (
  reportType: string,
  payload: any,
  userMeta: UserMeta,
  selectedUserIds: number[] = [],
): ParsedMfReport => {
  let rows: any[] = [];
  let totalHoldingInfo: Record<string, unknown> | null = null;
  let totalPages = 1;
  let pageIndex = 1;
  let totalRecords = 0;
  let taxTotalGainLoss: number | null = null;

  switch (reportType) {
    case "Holding": {
      const dataRows = Array.isArray(payload?.data) ? payload.data : [];
      rows = dataRows
        .map(normalizeUnrealisedRow)
        .map(enrichUnrealizedHoldingRow);
      totalHoldingInfo = payload?.totalHoldingInfo ?? null;
      rows = filterPositiveUnits(rows);
      break;
    }
    case "Realized": {
      const dataRows = Array.isArray(payload?.data) ? payload.data : [];
      rows = dataRows.map(normalizeRealisedRow);
      break;
    }
    case "All": {
      const holdings = Array.isArray(payload?.data) ? payload.data : [];
      totalHoldingInfo = payload?.totalHoldingInfo ?? null;
      const unrealized = filterPositiveUnits(holdings).map((h) =>
        normalizeHoldingRowForAll(h),
      );
      const realized = filterRealizedTxnRows(
        flattenMfHoldingsTransactions(holdings),
      ).map((t) => normalizeTxnRowForAll(t));
      rows = [...unrealized, ...realized];
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = Number(payload?.totalCount) || rows.length;
      break;
    }
    case "Transaction": {
      const holdings = Array.isArray(payload?.data) ? payload.data : [];
      totalHoldingInfo = payload?.totalHoldingInfo ?? null;
      rows = flattenMfHoldingsTransactions(holdings);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = Number(payload?.totalCount) || rows.length;
      break;
    }
    case "Transaction report": {
      const dataRows = Array.isArray(payload?.response) ? payload.response : [];
      rows = sortMfTransactionsByDateDesc(dataRows.map(mapTransactionDetailsRow));
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = Number(payload?.totalRecords) || rows.length;
      break;
    }
    case "Sector report":
    case "Cap report": {
      const raw = Array.isArray(payload) ? payload : payload?.data ?? [];
      rows = flattenSectorCapRows(raw);
      break;
    }
    case "Weekly Returns": {
      rows = sortWeeklyReturnsRows(
        extractWeeklyReturnsRows(payload).map(normalizeWeeklyReturnsRow),
      );
      break;
    }
    case "Point to Point Return": {
      rows = Array.isArray(payload?.data) ? payload.data : [];
      totalHoldingInfo = payload?.cagr != null ? { cagr: payload.cagr } : null;
      break;
    }
    case "Short Term Capital Gain":
    case "Long Term Capital Gain": {
      const parsed = parseStockTaxReport(payload?.response ?? payload);
      rows = parsed ? extractStockTaxRows(parsed, reportType) : [];
      taxTotalGainLoss = extractStockTaxSummaryTotal(parsed, reportType);
      break;
    }
    default:
      rows = [];
  }

  return {
    rows: attachInvestorMeta(rows, userMeta, selectedUserIds),
    totalHoldingInfo,
    totalPages,
    pageIndex,
    totalRecords,
    taxTotalGainLoss,
  };
};

export const mergeTransactionPages = (existing: any[], next: any[]) => {
  const seen = new Set(
    existing.map(
      (r) =>
        `${r.userId}-${r.schemeName}-${r.transactiondate}-${r.folioNo}-${r.units}`,
    ),
  );
  const merged = [...existing];
  for (const row of next) {
    const key = `${row.userId}-${row.schemeName}-${row.transactiondate}-${row.folioNo}-${row.units}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(row);
    }
  }
  return sortMfTransactionsByDateDesc(merged);
};
